// Eco award — "Oak Status" growing-oak model. Single source of truth for
// the stage vocabulary so every consumer (Badge, override panel, award
// path, supplier portal) agrees.
//
// The Eco award grows as the supplier's required eco documents are approved:
//   progress = approved / required   (required capped at 5)
//   frame    = round(progress × 5)   → a clean visible step on every approval
//
// Stage index === oak frame (0..5). 'acorn' at 0 approved; 'oak_status' the
// badge of honour at 100%. No named mid-tiers — callers show plain progress
// ("3 of 5 eco documents approved · 60%") in between.
//
// Stored in supplier_badges.tier for the Eco category (the tier CHECK was
// widened to allow these — see 2026-06-eco-award-oak-stages.sql). Bronze/
// Silver/Gold are retired for Eco; other categories keep their tiers.

export const ECO_REQUIRED_CAP = 5;

// Ordered low → high; the array index is the oak frame.
export const ECO_STAGES = ['acorn', 'first_shoot', 'seedling', 'young_oak', 'maturing_oak', 'oak_status'];

export const ECO_STAGE_LABELS = {
  acorn:        'Acorn',
  first_shoot:  'First Shoot',
  seedling:     'Seedling',
  young_oak:    'Young Oak',
  maturing_oak: 'Maturing Oak',
  oak_status:   'Oak Status',
};

export const OAK_STATUS = 'oak_status';
export const ECO_ACORN = 'acorn';

export const isEcoStage = (v) => ECO_STAGES.includes(v);
export const isOakStatus = (stage) => stage === OAK_STATUS;

// Stage → oak frame (0..5). Unknown / null → 0 (acorn).
export const ecoStageToFrame = (stage) => {
  const i = ECO_STAGES.indexOf(stage);
  return i < 0 ? 0 : i;
};

// Oak frame → stage. Clamped to the valid range.
export const ecoFrameToStage = (frame) => ECO_STAGES[Math.max(0, Math.min(ECO_STAGES.length - 1, frame | 0))];

export const ecoStageLabel = (stage) => ECO_STAGE_LABELS[stage] || ECO_STAGE_LABELS.acorn;

// Map an approved/required pair to a stage.
//   0 approved                → acorn
//   approved >= required (>0) → oak_status   (1 required doc jumps straight here)
//   in between               → round(progress × 5), held to 1..4 so the
//                              extremes stay reserved for acorn / oak_status.
// With required capped at 5 and approved ≥ 1, the intermediate round never
// lands on 0 or 5 on its own, but we clamp defensively.
export const ecoStageFromProgress = (approved, required) => {
  const a = Number(approved) || 0;
  const r = Math.min(Number(required) || 0, ECO_REQUIRED_CAP);
  if (r <= 0 || a <= 0) return ECO_ACORN;
  if (a >= r) return OAK_STATUS;
  const frame = Math.round((a / r) * 5);
  return ECO_STAGES[Math.max(1, Math.min(4, frame))];
};

// Plain-progress line for the mid-states. Callers decide whether to show it
// (only between acorn and oak_status).
export const ecoProgressLabel = (approved, required) => {
  const a = Number(approved) || 0;
  const r = Math.min(Number(required) || 0, ECO_REQUIRED_CAP);
  if (r <= 0) return null;
  const pct = Math.round((Math.min(a, r) / r) * 100);
  return `${Math.min(a, r)} of ${r} eco document${r === 1 ? '' : 's'} approved · ${pct}%`;
};

// ----- Live eco-document counting (approved ÷ required) ----------------------
// Counts the supplier's REQUIRED eco documents and how many are approved.
// "Required" = eco-category documents (fusion_documents.badge_category_id =
// eco) that are applicable to the supplier — the same risk-based applicability
// the compliance award uses: (risk-tier requirements ∪ per-supplier overrides)
// − not_required — intersected with the eco doc set, capped at 5.
export const computeEcoApproval = async (supabase, supplierId, riskFlag) => {
  const { data: cat } = await supabase.from('badge_categories').select('id').eq('slug', 'eco').maybeSingle();
  if (!cat) return { approved: 0, required: 0, ecoCategoryId: null };
  const { data: ecoFds } = await supabase
    .from('fusion_documents').select('compliance_doc_key').eq('badge_category_id', cat.id);
  const ecoKeys = new Set((ecoFds || []).map(f => f.compliance_doc_key).filter(Boolean));
  if (ecoKeys.size === 0) return { approved: 0, required: 0, ecoCategoryId: cat.id };

  let tierKeys = new Set();
  if (riskFlag) {
    const { data: reqs } = await supabase
      .from('risk_document_requirements').select('document_key').eq('risk_level', riskFlag);
    tierKeys = new Set((reqs || []).map(r => r.document_key));
  }
  const { data: records } = await supabase
    .from('supplier_onboarding_documents')
    .select('document_key, expiry_approved_at, not_required, risk_override_applicable')
    .eq('supplier_id', supplierId);
  const recs = records || [];
  const notRequired = new Set(recs.filter(r => r.not_required).map(r => r.document_key));
  const required = new Set();
  for (const k of tierKeys) if (!notRequired.has(k)) required.add(k);
  for (const r of recs) if (r.risk_override_applicable && !notRequired.has(r.document_key)) required.add(r.document_key);

  const ecoRequiredKeys = [...required].filter(k => ecoKeys.has(k));
  const approvedKeys = new Set(recs.filter(r => r.expiry_approved_at).map(r => r.document_key));
  const requiredCount = Math.min(ecoRequiredKeys.length, ECO_REQUIRED_CAP);
  const approvedCount = Math.min(ecoRequiredKeys.filter(k => approvedKeys.has(k)).length, requiredCount);
  return { approved: approvedCount, required: requiredCount, ecoCategoryId: cat.id };
};

// Recompute + persist the supplier's Eco oak stage from live doc approvals.
// Upserts the eco supplier_badges row (tier = stage). Celebration (first_seen_at
// null) fires ONLY when the supplier NEWLY reaches Oak Status; intermediate
// stages stamp first_seen_at so the earn modal stays quiet. Returns the stage,
// or null when the supplier has no required eco docs (nothing to award).
export const recomputeEcoBadge = async (supabase, supplierId, riskFlag) => {
  let rf = riskFlag;
  if (rf === undefined) {
    const { data: sup } = await supabase.from('suppliers').select('risk_flag').eq('id', supplierId).maybeSingle();
    rf = sup?.risk_flag || null;
  }
  const { approved, required, ecoCategoryId } = await computeEcoApproval(supabase, supplierId, rf);
  if (!ecoCategoryId || required <= 0) return null;
  const liveStage = ecoStageFromProgress(approved, required);
  const liveIndex = ecoStageToFrame(liveStage);
  const { data: existing } = await supabase
    .from('supplier_badges').select('id, tier, awarded_at, first_seen_at')
    .eq('supplier_id', supplierId).eq('category_id', ecoCategoryId).maybeSingle();
  const existingIndex = existing ? ecoStageToFrame(existing.tier) : 0;
  // HOLD: never visibly step the oak back. Eco documents don't expire, so the
  // oak only ever moves when Fusion issues a NEW/REFRESHED eco document, which
  // drops live progress below the stage already reached. We keep the displayed
  // stage at the highest reached and flag action_needed instead of demoting;
  // it clears when the supplier completes the new doc and live catches up.
  const heldIndex = existing ? Math.max(existingIndex, liveIndex) : liveIndex;
  const heldStage = ecoFrameToStage(heldIndex);
  const actionNeeded = liveIndex < heldIndex;
  // Celebrate only when the supplier GENUINELY reaches Oak Status by real
  // progress (not when the held picture sits at oak during an action-needed
  // hold) — and never re-fire once they've already seen it.
  const newlyOak = liveStage === OAK_STATUS && existing?.tier !== OAK_STATUS;
  const firstSeen = newlyOak ? null : (existing?.first_seen_at ?? new Date().toISOString());
  // awarded_at marks when the held stage was reached — bump only when the oak
  // actually grows (a drop into action-needed hold preserves it).
  const grew = !existing || heldIndex > existingIndex;
  const payload = {
    supplier_id: supplierId,
    category_id: ecoCategoryId,
    tier: heldStage,
    awarded_at: grew ? new Date().toISOString() : existing.awarded_at,
    awarded_by_document_id: null,
    expires_at: null, // eco never expires — no time-based decay
    first_seen_at: firstSeen,
    action_needed: actionNeeded,
  };
  if (existing) await supabase.from('supplier_badges').update(payload).eq('id', existing.id);
  else await supabase.from('supplier_badges').insert([payload]);
  // Return the held stage plus whether this recompute is the moment the
  // supplier GENUINELY reached Oak Status (drives the Captain-Otto
  // celebration in the admin compliance review). No caller relied on the
  // previous bare-string return.
  return { stage: heldStage, newlyOak };
};
